<template>
	<div>
		<SelectionModal
			:title="$t('admin_groups_add_members_action')"
			:confirmText="confirmButtonText"
			:confirmProps="{ loading: confirming }"
			:items="users"
			:columns="columns"
			:selectedItemsById="selectedUsersById"
			:loading="fetchingUsers"
			:loadingMore="loadingMore"
			:maxLimit="group ? group.memberships_available : 1000"
			class="add-members"
			@selection="onSelectionChange"
			@load-more="loadMore"
			@confirm="addMembersToGroup"
			@cancel="onClose"
			ref="modal"
		>
			<template #cell="{ data, selected }">
				<div class="user-content">
					<UserCell class="user-info" :user="data" hideTag />
					<div
						class="already-selected"
						v-if="selected === SELECTION_FIXED"
						v-t="'admin_groups_already_group_member_label'"
					/>
				</div>
			</template>
			<template #filters>
				<div class="filters">
					<Input
						:placeholder="$t('admin_people_search_action')"
						class="search"
						v-model="query"
					/>
				</div>
			</template>
			<template #footer-max>
				<span
					class="at-max-limit"
					v-text="$t('admin_people_add_person_max_limit')"
				/>
			</template>
		</SelectionModal>

		<BulkErrorModal
			v-if="bulkErrors"
			:successCount="bulkErrors.successCount"
			:errorCount="bulkErrors.errorCount"
			:errors="bulkErrors.errors"
			@confirm="onClose"
			@cancel="onClose"
		>
			<template #error-item-left="{ data }">
				<UserCell :user="data.user" hideTag />
			</template>
		</BulkErrorModal>
	</div>
</template>

<script>
import { Table, Input } from '@/components/common'
import { get, post, SelectionModal, UserCell, BulkErrorModal } from '../common'
import debounce from 'debounce'

const PAGE_SIZE = 30

export default {
	name: 'AddMembersModal',
	components: {
		Input,
		SelectionModal,
		UserCell,
		BulkErrorModal,
	},

	props: {
		group: Object,
	},

	data() {
		return {
			columns: [{ field: 'fullname' }],
			confirming: false,
			users: [],
			selectedUsersById: {},
			fetchingUsers: false,
			loadingMore: false,
			query: null,
			bulkErrors: null,
			SELECTION_FIXED: Table.SELECTION_FIXED,
		}
	},

	emits: ['cancel', 'success'],

	created() {
		this.fetchUsersDebounced = debounce(this.fetchUsers.bind(this), 500)
		this.fixedSelectionsById = {}
	},

	async mounted() {
		await this.fetchUsers()
	},

	watch: {
		query() {
			this.fetchingUsers = true
			this.fetchUsersDebounced()
		},
	},

	computed: {
		selectedCount() {
			return Object.values(this.selectedUsersById).filter(
				(x) => x.selected === true
			).length
		},
		confirmButtonText() {
			return this.selectedCount === 0
				? this.$t('admin_groups_add_people_button')
				: this.$tc(
						'admin_groups_add_people_button_count.variable',
						this.selectedCount,
						{
							Count: this.selectedCount,
						}
				  )
		},
	},

	methods: {
		onClose() {
			this.bulkErrors = null
			this.$emit('cancel')
		},

		onSelectionChange(selected = {}) {
			this.selectedUsersById = Object.assign(
				{},
				selected,
				this.fixedSelectionsById
			)
		},

		setFixedSelections() {
			const { users_with_chat, data: users } = this.apiResponse
			if (!users_with_chat?.length) return
			this.fixedSelectionsById = users_with_chat
				.map((id) => users.find((u) => u.id === id))
				.filter((u) => u)
				.reduce((acc, u) => {
					acc[u.id] = { item: u, selected: Table.SELECTION_FIXED }
					return acc
				}, this.fixedSelectionsById)
			this.selectedUsersById = Object.assign(
				{},
				this.selectedUsersById,
				this.fixedSelectionsById
			)
		},

		async addMembersToGroup() {
			this.confirming = true
			try {
				const user_ids = Object.values(this.selectedUsersById)
					.filter((x) => x.selected === true)
					.map((x) => x.item.id)
				await post(`/organization/groups/${this.group.id}/memberships/batch`, {
					user_ids,
				})
				this.$alert.success(
					this.$tc(
						'admin_people_add_members_success_label.variable',
						user_ids.length,
						{ Count: user_ids.length }
					)
				)
				this.$emit('success')
				this.onClose()
			} catch (e) {
				console.error(e)
				this.bulkErrors = this.processBulkErrors(e)
			}
			this.confirming = false
		},

		processBulkErrors(e) {
			// Since a single user might appear in multiple error code
			// groupings in the error reponse, we want to normalize those so
			// a given user only appears once in the list of errors but with
			// a message for each code
			const { data } = e.response
			// get flat list of all errors
			const allErrors = data.errors.user_ids
				.map((error) =>
					error.user_ids.map((user_id) => ({
						error,
						user_id,
					}))
				)
				.flat()
			// group all errors by user_id
			const errorsByUserId = allErrors.reduce((acc, { error, user_id }) => {
				if (acc[user_id]) {
					acc[user_id].messages.push(error.message)
					return acc
				}
				acc[user_id] = {
					user: this.users.find((g) => g.id === user_id),
					messages: [error.message],
				}
				return acc
			}, {})
			const errorsToDisplay = Object.values(errorsByUserId)
			return {
				successCount: data.success?.user_ids?.length ?? 0,
				errorCount: errorsToDisplay.length,
				errors: errorsToDisplay,
			}
		},

		async fetchUsers() {
			this.fetchingUsers = true
			const params = {
				sort: 'firstname,lastname',
				'filter[active]': true,
				'page[size]': PAGE_SIZE,
			}
			if (this.query) {
				params['filter[search]'] = this.query
			}
			if (this.group) {
				params['additional[memberships.chat_id]'] = this.group.id
			}
			this.apiResponse = await get('/organization/users', params)
			this.users = this.apiResponse.data
			this.setFixedSelections()
			this.fetchingUsers = false
			this.$refs.modal.$refs.mainTable.scrollToTop()
		},

		async loadMore() {
			if (!this.apiResponse?.next || this.loadingMore) return
			try {
				this.loadingMore = true
				this.apiResponse = await this.apiResponse.next()
				this.users = this.users.concat(this.apiResponse.data)
				this.setFixedSelections()
			} catch (e) {
				console.error(e)
			}
			await this.$nextTick()
			this.loadingMore = false
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.filters {
	width: 100%;
}
.user-content {
	@include flex-spread;
	width: 100%;
	.user-info {
		flex-shrink: 0;
		flex-grow: 1;
		@include ellipsis;
	}
	.already-selected {
		font-style: italic;
		flex-shrink: 0;
		margin-left: 8px;
		&::after {
			// last letter in italic word was getting cut off :(
			content: ' ';
			margin-right: 4px;
		}
	}
}
.at-max-limit {
	margin-left: 24px;
	max-width: 400px;
	@include ellipsis;
	font-weight: normal;
	color: var(--neutral-400);
}
</style>

<style lang="scss">
.modal-content {
	.table-cell-content {
		width: 100%;
	}
}
</style>
